import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { createColumnHelper } from "@tanstack/react-table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload, faInfo } from "@fortawesome/free-solid-svg-icons";
import { getPrimaryActiveResource, renderUserName } from "../../../utils/helpers/user";
import { useCompanyDateFormat, useCompanyTimeFormat } from "../../../utils/hooks/company";
import moment from "moment-timezone";
import { useUser } from "../../../utils/hooks/user";
import { renderDurationAsFormat } from "../../../utils/helpers/date";

const columnHelper = createColumnHelper();

export const useColumns = ({ open }) => {
    const { t } = useTranslation();
    const dateFormat = useCompanyDateFormat();
    const timeFormat = useCompanyTimeFormat();
    const user = useUser();

    const primaryLocation = useMemo(
        () => getPrimaryActiveResource(user?.locations || []),
        [user?.locations]
    );

    return useMemo(() => {
        const columns = [
            columnHelper.accessor("type", {
                enableSorting: false,
                header: t("type"),
                cell: (info) => {
                    const type = info.getValue();
                    return <div className="text-uppercase">{t(type)}</div>
                }
            }),
            columnHelper.accessor("createdAt", {
                enableSorting: false,
                header: t("created-at"),
                cell: (info) => {
                    const createdAt = info.getValue();
                    const timezone = primaryLocation?.timezone || "UTC";

                    return createdAt && (
                        <>
                            {moment(createdAt).tz(timezone).format(dateFormat)} -{" "}
                            {moment(createdAt).tz(timezone).format(timeFormat)}
                        </>
                    );
                },
            }),
            columnHelper.accessor("completedAt", {
                enableSorting: false,
                header: t("completed-at"),
                cell: (info) => {
                    const completedAt = info.getValue();
                    const timezone = primaryLocation?.timezone || "UTC";
                    return completedAt && (
                        <>
                            {moment(completedAt).tz(timezone).format(dateFormat)} -{" "}
                            {moment(completedAt).tz(timezone).format(timeFormat)}
                        </>
                    );
                },
            }),
            columnHelper.accessor("completed", {
                enableSorting: false,
                header: t("completed"),
                cell: (info) => {
                    const completed = info.getValue();
                    return (
                        <div
                            className="px-3 py-2 text-green font-weight-bold text-center"
                            style={{ borderRadius: "10px", background: "#48bbc326" }}
                        >
                            {completed || 0}
                        </div>
                    );
                },
            }),
            columnHelper.accessor("failed", {
                enableSorting: false,
                header: t("failed"),
                cell: (info) => {
                    const failed = info.getValue();
                    return (
                        <div
                            className="px-3 py-2 text-danger font-weight-bold text-center"
                            style={{ borderRadius: "10px", background: "#ff00001a" }}
                        >
                            {failed || 0}
                        </div>
                    );
                },
            }),
            columnHelper.accessor("totalTime", {
                enableSorting: false,
                header: t("duration"),
                cell: (info) => {
                    const duration = info.getValue();
                    return renderDurationAsFormat((duration / 1000) || 0);
                },
            }),
            columnHelper.accessor("status", {
                enableSorting: false,
                header: t("status"),
                cell: (info) => t(info.getValue()),
            }),
            columnHelper.display({
                id: "actions",
                header: t("actions"),
                cell: (info) => {
                    const data = info.row.original?.result || [];
                    const type = info.row.original?.type;
                    const status = info.row.original?.status;
                    const failed = info.row.original?.failed;

                    return status === "completed" && (failed > 0 || type === "import") && (
                        <div className="d-flex align-items-center justify-content-center">
                            <FontAwesomeIcon
                                className="cursor-pointer px-2  py-1"
                                icon={faInfo}
                                onClick={() => open({ type, data })}
                            />
                        </div>
                    )
                },
            }),
        ];
        return columns;
    }, [t, primaryLocation?.timezone, dateFormat, timeFormat, open]);
};
